import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, useTheme, API } from '@/App';
import { useTranslation } from '@/hooks/useTranslation';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import Sidebar from '@/components/Sidebar';
import WhatsAppEmbeddedSignup from '@/components/WhatsAppEmbeddedSignup';
import { FaWhatsapp, FaFacebook } from 'react-icons/fa';
import {
  Menu,
  Facebook,
  Instagram,
  MessageCircle,
  MessageSquare,
  Globe,
  CheckCircle,
  XCircle,
  Loader2,
  Unlink,
  Code,
  Copy,
  Check,
  AlertCircle,
  ExternalLink,
  PlayCircle,
  Send,
  QrCode,
  Zap,
  Settings,
  Radio,
  Bot,
  Link2,
  ChevronRight
} from 'lucide-react';

// Compact Available Channel Card
const AvailableChannelCard = ({
  icon: Icon,
  iconBgColor = 'bg-slate-500',
  name,
  description,
  onConnect,
  connectLabel = 'Connect',
  features = [],
  extraAction,
}) => (
  <div className="flex h-full flex-col bg-white dark:bg-zinc-900 rounded-xl border border-slate-100 dark:border-zinc-800/60 p-4 sm:p-5 hover:shadow-md transition-all duration-200 group" data-testid={`channel-card-${name.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-10 h-10 ${iconBgColor} rounded-xl flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-zinc-50 leading-tight">{name}</h3>
        <p className="text-xs text-slate-500 dark:text-zinc-400 line-clamp-1 mt-0.5">{description}</p>
      </div>
    </div>
    {features.length > 0 && (
      <div className="flex flex-1 flex-wrap content-start gap-x-3 gap-y-1 mb-3">
        {features.map((f, i) => (
          <span key={i} className="text-[11px] text-slate-500 dark:text-zinc-400 flex items-center gap-1">
            <CheckCircle className="w-3 h-3 text-emerald-500 shrink-0" />{f}
          </span>
        ))}
      </div>
    )}
    <Button
      onClick={onConnect}
      className="mt-auto w-full h-9 gap-1.5 bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-slate-200 dark:text-slate-900 text-white text-xs font-medium rounded-xl"
      data-testid={`connect-${name.toLowerCase().replace(/\s+/g, '-')}-btn`}
    >
      {connectLabel}
      <ChevronRight className="w-3.5 h-3.5" />
    </Button>
    {extraAction && <div className="mt-2">{extraAction}</div>}
  </div>
);

// Snapshot in-memory hasil kunjungan terakhir (per user). Status channel harus
// selalu diambil segar dari network, jadi snapshot ini hanya dipakai untuk
// menggambar halaman seketika saat dibuka lagi — respons segar menimpanya.
let channelsSnapshot = null; // { userId, results }
const getChannelsSnapshot = (userId) =>
  channelsSnapshot && userId && channelsSnapshot.userId === userId ? channelsSnapshot.results : null;

const ChannelsPage = () => {
  const { user, token } = useAuth();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(() => !getChannelsSnapshot(user?.id));
  const [chatbots, setChatbots] = useState([]);
  
  // Channel states
  const [messengerStatus, setMessengerStatus] = useState(null);
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [instagramStatus, setInstagramStatus] = useState(null);
  
  // Selected agents
  const [messengerAgent, setMessengerAgent] = useState('');
  const [whatsappAgent, setWhatsappAgent] = useState('');
  const [instagramAgent, setInstagramAgent] = useState('');
  const [websiteAgent, setWebsiteAgent] = useState('');
  
  // Loading states
  const [savingMessenger, setSavingMessenger] = useState(false);
  const [savingWhatsapp, setSavingWhatsapp] = useState(false);
  const [savingInstagram, setSavingInstagram] = useState(false);
  const [savingWebsite, setSavingWebsite] = useState(false);
  const [disconnectingMessenger, setDisconnectingMessenger] = useState(false);
  const [disconnectingWhatsapp, setDisconnectingWhatsapp] = useState(false);
  const [disconnectingInstagram, setDisconnectingInstagram] = useState(false);
  const [subscribingWebhook, setSubscribingWebhook] = useState(false);
  
  // Embed modal
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [embedCode, setEmbedCode] = useState('');
  const [copied, setCopied] = useState(false);
  
  // WhatsApp Method Selection Modal
  const [showWaMethodModal, setShowWaMethodModal] = useState(false);
  const [waMethod, setWaMethod] = useState('nerava');
  const [ownPhoneNumber, setOwnPhoneNumber] = useState('');
  const [waEmbeddedStatus, setWaEmbeddedStatus] = useState(null);
  const [waEmbeddedAgent, setWaEmbeddedAgent] = useState('');
  const [savingEmbeddedAgent, setSavingEmbeddedAgent] = useState(false);
  const [disconnectingEmbedded, setDisconnectingEmbedded] = useState(false);
  const [tiktokStatus, setTiktokStatus] = useState(null);
  const [tiktokAgent, setTiktokAgent] = useState('');
  const [savingTiktokAgent, setSavingTiktokAgent] = useState(false);
  
  // WhatsApp QR Code states
  const [qrLoading, setQrLoading] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [qrSessionId, setQrSessionId] = useState(null);
  const [qrStatus, setQrStatus] = useState(null);
  const [qrPollingInterval, setQrPollingInterval] = useState(null);
  const [hasShownQrToast, setHasShownQrToast] = useState(false);
  
  // WhatsApp QR Connection status
  const [whatsappQrStatus, setWhatsappQrStatus] = useState(null);
  const [fbSdkReady, setFbSdkReady] = useState(false);
  
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [searchParams, setSearchParams] = useSearchParams();

  // Load Facebook SDK for Messenger connection
  useEffect(() => {
    if (!document.getElementById('facebook-jssdk')) {
      window.fbAsyncInit = function() {
        window.FB.init({
          appId: process.env.REACT_APP_FACEBOOK_APP_ID || '4201103220206383',
          autoLogAppEvents: true,
          xfbml: false,
          version: 'v21.0'
        });
        setFbSdkReady(true);
      };
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    } else if (window.FB) {
      setFbSdkReady(true);
    }
  }, []);

  // Handle OAuth callback success/error from URL params
  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const username = searchParams.get('username');
    const message = searchParams.get('message');
    const detail = searchParams.get('detail');
    const action = searchParams.get('action');
    
    if (success === 'instagram_connected') {
      toast.success(`Instagram ${username ? '@' + username : ''} connected successfully!`);
      subscribeInstagramWebhook();
      setSearchParams({});
      fetchAllData();
    }
    
    if (error) {
      const errorMessages = {
        'auth_failed': 'Autentikasi gagal. Silakan coba lagi.',
        'token_exchange_failed': 'Gagal mendapatkan token. Silakan coba lagi.',
        'no_access_token': 'Token akses tidak ditemukan.',
        'failed_to_get_pages': 'Gagal mengambil daftar halaman Facebook.',
        'no_pages_found': 'Tidak ada Facebook Page ditemukan. Buat halaman dulu di Facebook.',
        'invalid_state': 'Sesi tidak valid. Silakan coba lagi.',
        'callback_failed': 'Proses autentikasi gagal. Silakan coba lagi.',
        'instagram_auth_failed': message || 'Autentikasi Instagram gagal',
        'instagram_no_code': 'No authorization code from Instagram',
        'instagram_token_exchange_failed': `Failed to exchange token: ${detail || 'Unknown error'}`,
      };
      toast.error(errorMessages[error] || message || `Error: ${error}`);
      setSearchParams({});
    }

    if (action === 'select-page') {
      navigate('/messenger-setup?step=select-page', { replace: true });
    }
  }, [searchParams]);

  useEffect(() => {
    const snapshot = getChannelsSnapshot(user?.id);
    if (snapshot) fetchAllData(snapshot); // gambar instan dari kunjungan sebelumnya
    fetchAllData(null, !!snapshot); // refresh senyap di belakang
    fetchWhatsappQrStatus();
  }, []);

  const fetchAllData = async (preloaded = null, silent = false) => {
    try {
      if (!preloaded && !silent) setLoading(true);
      
      const [chatbotsRes, messengerRes, instagramRes, whatsappRes, channelsRes, whatsappQrRes, waEmbeddedRes, tiktokRes] = preloaded || await Promise.all([
        axios.get(`${API}/chatbots`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/messenger/status`, { headers }).catch(() => ({ data: { connected: false } })),
        axios.get(`${API}/instagram/status`, { headers }).catch(() => ({ data: { connected: false } })),
        axios.get(`${API}/whatsapp/connection-status`, { headers }).catch(() => ({ data: { connected: false } })),
        axios.get(`${API}/channels`, { headers }).catch(() => ({ data: { channels: [] } })),
        axios.get(`${API}/whatsapp-qr/connection`, { headers }).catch(() => ({ data: { connected: false } })),
        axios.get(`${API}/whatsapp-embedded/status`, { params: { user_id: user?.id }, headers }).catch(() => ({ data: { connected: false } })),
        axios.get(`${API}/tiktok/status`, { headers }).catch(() => ({ data: { connected: false } }))
      ]);
      
      if (!preloaded) {
        channelsSnapshot = {
          userId: user?.id,
          results: [chatbotsRes, messengerRes, instagramRes, whatsappRes, channelsRes, whatsappQrRes, waEmbeddedRes, tiktokRes]
            .map((r) => ({ data: r?.data })),
        };
      }

      setChatbots(chatbotsRes.data || []);
      setMessengerStatus(messengerRes.data);
      setInstagramStatus(instagramRes.data);
      
      if (whatsappQrRes.data?.connected) {
        setWhatsappQrStatus(whatsappQrRes.data);
      }
      
      if (waEmbeddedRes.data?.connected) {
        setWaEmbeddedStatus(waEmbeddedRes.data);
        setWaEmbeddedAgent(waEmbeddedRes.data.chatbot_id || '');
      }
      
      if (tiktokRes.data?.connected) {
        setTiktokStatus(tiktokRes.data);
        setTiktokAgent(tiktokRes.data.connection?.chatbot_id || '');
      }
      
      const waData = whatsappRes.data;
      if (waData?.connected) {
        setWhatsappStatus({
          connected: true,
          mode: waData.mode,
          store_name: waData.store_name,
          chatbot_id: waData.chatbot_id,
          chatbot_name: waData.chatbot_name,
          routing_keyword: waData.routing_keyword,
          whatsapp_link: waData.whatsapp_link,
          shared_phone: waData.shared_phone,
          own_phone: waData.own_phone,
          own_phone_status: waData.own_phone_status
        });
        setWhatsappAgent(waData.chatbot_id || '');
      } else {
        setWhatsappStatus({ 
          connected: false, 
          shared_available: waData?.shared_available,
          shared_phone: waData?.shared_phone 
        });
      }
      
      if (messengerRes.data?.chatbot_id) {
        setMessengerAgent(messengerRes.data.chatbot_id);
      }
      
      if (instagramRes.data?.chatbot_id) {
        setInstagramAgent(instagramRes.data.chatbot_id);
      }
      
      const channels = channelsRes.data?.channels || [];
      const instagram = channels.find(c => c.channel_type === 'instagram');
      
      if (instagram) {
        setInstagramStatus({ connected: true, ...instagram });
        setInstagramAgent(instagram.chatbot_id || '');
      }
      
      if (chatbotsRes.data?.length > 0) {
        setWebsiteAgent(chatbotsRes.data[0].id);
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status !== 401) {
        toast.error('Failed to load channel data');
      }
    } finally {
      setLoading(false);
    }
  };

  // Messenger handlers
  const handleConnectMessenger = () => {
    if (!user?.id) {
      toast.error('Silakan login terlebih dahulu');
      return;
    }
    if (window.FB) {
      window.FB.login((response) => {
        if (response.authResponse) {
          axios.post(`${backendUrl}/api/messenger/oauth/sdk-login`,
            { access_token: response.authResponse.accessToken },
            { headers }
          ).then(res => {
            if (res.data.success && res.data.pages) {
              toast.success('Login Facebook berhasil! Pilih halaman yang ingin dihubungkan.');
              navigate('/messenger-setup?step=select-page');
            }
          }).catch(err => {
            toast.error(err.response?.data?.detail || 'Gagal menghubungkan Facebook');
          });
        } else {
          toast.error('Login Facebook dibatalkan');
        }
      }, {
        scope: 'pages_show_list,pages_messaging,pages_manage_metadata',
        return_scopes: true
      });
    } else {
      toast.error('Facebook SDK belum dimuat. Silakan refresh halaman dan coba lagi.');
    }
  };

  // Auto-save agent handlers - saves immediately on dropdown change
  const handleAutoSaveMessengerAgent = async (newAgentId) => {
    setMessengerAgent(newAgentId);
    if (!newAgentId) return;
    try {
      setSavingMessenger(true);
      await axios.put(`${API}/messenger/update-agent`, { chatbot_id: newAgentId }, { headers });
      toast.success('AI Agent Messenger tersimpan');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan AI Agent');
    } finally {
      setSavingMessenger(false);
    }
  };

  const handleDisconnectMessenger = async () => {
    if (!confirm('Yakin ingin memutuskan Messenger?')) return;
    try {
      setDisconnectingMessenger(true);
      await axios.delete(`${API}/messenger/disconnect`, { headers });
      toast.success('Messenger disconnected');
      setMessengerStatus({ connected: false });
      setMessengerAgent('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal disconnect Messenger');
    } finally {
      setDisconnectingMessenger(false);
    }
  };

  // WhatsApp handlers
  const [showWhatsappConnectModal, setShowWhatsappConnectModal] = useState(false);
  const [showOwnNumberModal, setShowOwnNumberModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [connectingWhatsapp, setConnectingWhatsapp] = useState(false);
  const [ownNumberConfirmed, setOwnNumberConfirmed] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  
  const userStoreName = user?.store_name || '';
  
  useEffect(() => {
    return () => {
      if (qrPollingInterval) clearInterval(qrPollingInterval);
    };
  }, [qrPollingInterval]);
  
  const fetchWhatsappQrStatus = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp-qr/connection`, { headers });
      setWhatsappQrStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch WhatsApp QR status:', error);
    }
  };
  
  const startQRSession = async () => {
    setQrLoading(true);
    setHasShownQrToast(false);
    try {
      const response = await axios.post(`${API}/whatsapp-qr/start-session`, {}, { headers });
      if (response.data.status === 'connected') {
        setQrStatus('connected');
        setWhatsappQrStatus({ connected: true, phone_number: response.data.phone_number, phone_name: response.data.phone_name });
        if (!hasShownQrToast) { toast.success('WhatsApp connected!'); setHasShownQrToast(true); }
      } else {
        setQrCode(response.data.qr_code);
        setQrSessionId(response.data.session_id);
        setQrStatus('pending');
        const interval = setInterval(() => checkQRStatus(response.data.session_id), 3000);
        setQrPollingInterval(interval);
      }
    } catch (error) {
      toast.error('Failed to start QR session. Coba lagi.');
    } finally {
      setQrLoading(false);
    }
  };
  
  const checkQRStatus = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/whatsapp-qr/status/${sessionId}`, { headers });
      const newStatus = response.data.status;
      if (response.data.qr_code && newStatus === 'qr_ready') setQrCode(response.data.qr_code);
      if (newStatus === 'connected' && qrStatus !== 'connected') {
        if (qrPollingInterval) clearInterval(qrPollingInterval);
        setQrPollingInterval(null);
        setQrStatus('connected');
        setWhatsappQrStatus({ connected: true, phone_number: response.data.phone_number, phone_name: response.data.phone_name });
        if (!hasShownQrToast) { toast.success('WhatsApp connected via QR!'); setHasShownQrToast(true); }
        setShowWaMethodModal(false);
        fetchAllData();
      } else if (newStatus === 'expired' || newStatus === 'disconnected') {
        if (qrPollingInterval) clearInterval(qrPollingInterval);
        setQrPollingInterval(null);
        setQrCode(null);
        setQrStatus(newStatus);
        toast.error('Sesi berakhir. Silakan generate QR baru.');
      } else {
        setQrStatus(newStatus);
      }
    } catch (error) {
      console.error('Status check error:', error);
    }
  };
  
  const handleDisconnectWhatsappQr = async () => {
    if (!confirm('Yakin ingin memutuskan WhatsApp QR?')) return;
    try {
      await axios.post(`${API}/whatsapp-qr/disconnect`, {}, { headers });
      toast.success('WhatsApp QR disconnected');
      setWhatsappQrStatus(null);
      setQrCode(null);
      setQrStatus(null);
      fetchAllData();
    } catch (error) {
      toast.error('Gagal disconnect WhatsApp QR');
    }
  };
  
  const handleCloseWaMethodModal = () => {
    if (qrPollingInterval) clearInterval(qrPollingInterval);
    setQrPollingInterval(null);
    setQrCode(null);
    setQrStatus(null);
    setShowWaMethodModal(false);
  };
  
  const handleSaveWaMethod = async () => {
    if (waMethod === 'qr_code') {
      if (qrStatus !== 'connected') { toast.error('Scan QR code terlebih dahulu'); return; }
      handleCloseWaMethodModal();
    } else if (waMethod === 'cloud_api') {
      handleCloseWaMethodModal();
      navigate('/whatsapp-onboarding');
    } else {
      handleCloseWaMethodModal();
      setShowWhatsappConnectModal(true);
    }
  };
  
  const handleConnectWhatsapp = () => {
    if (!chatbots || chatbots.length === 0) {
      toast.error('Buat AI Agent terlebih dahulu sebelum menghubungkan WhatsApp');
      navigate('/chatbots/new');
      return;
    }
    setShowWaMethodModal(true);
  };

  const handleConfirmConnectShared = async () => {
    if (!whatsappAgent) { toast.error('Pilih AI Agent terlebih dahulu'); return; }
    if (!userStoreName) { toast.error('Nama toko belum diisi. Isi di Pengaturan > Profil.'); return; }
    try {
      setConnectingWhatsapp(true);
      const response = await axios.post(`${API}/whatsapp/connect-shared`, { chatbot_id: whatsappAgent, store_name: userStoreName }, { headers });
      if (response.data?.success) {
        toast.success('WhatsApp berhasil diaktifkan!');
        setShowWhatsappConnectModal(false);
        setWhatsappStatus({ connected: true, mode: 'shared', ...response.data });
        fetchAllData();
      } else {
        toast.error('Gagal menghubungkan WhatsApp');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghubungkan WhatsApp');
    } finally {
      setConnectingWhatsapp(false);
    }
  };

  const handleRegisterOwnNumber = async () => {
    if (!ownPhoneNumber.trim()) { toast.error('Masukkan nomor WhatsApp'); return; }
    if (!userStoreName) { toast.error('Nama bisnis belum diisi. Isi di Pengaturan > Profil.'); return; }
    if (!ownNumberConfirmed) { toast.error('Anda harus menyetujui persyaratan'); return; }
    try {
      setConnectingWhatsapp(true);
      const response = await axios.post(`${API}/whatsapp/register-own-number`, {
        phone_number: ownPhoneNumber.trim(),
        chatbot_id: whatsappAgent || chatbots[0]?.id,
        business_name: userStoreName
      }, { headers });
      if (response.data?.success) {
        toast.success('Permintaan registrasi dikirim! Verifikasi 1-3 hari kerja.');
        setShowOwnNumberModal(false);
        setOwnPhoneNumber('');
        setOwnNumberConfirmed(false);
        fetchAllData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengirim permintaan');
    } finally {
      setConnectingWhatsapp(false);
    }
  };

  // Auto-save WhatsApp agent
  const handleAutoSaveWhatsappAgent = async (newAgentId) => {
    setWhatsappAgent(newAgentId);
    if (!newAgentId) return;
    try {
      setSavingWhatsapp(true);
      if (whatsappQrStatus?.connected) {
        await axios.put(`${API}/whatsapp-qr/update-chatbot`, { chatbot_id: newAgentId }, { headers });
      } else {
        await axios.put(`${API}/whatsapp/update-agent`, { chatbot_id: newAgentId }, { headers });
      }
      toast.success('AI Agent WhatsApp tersimpan');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan AI Agent');
    } finally {
      setSavingWhatsapp(false);
    }
  };

  const handleDisconnectWhatsapp = async () => {
    if (!confirm('Yakin ingin memutuskan WhatsApp?')) return;
    try {
      setDisconnectingWhatsapp(true);
      await axios.delete(`${API}/whatsapp/disconnect`, { headers });
      toast.success('WhatsApp disconnected');
      setWhatsappStatus({ connected: false });
      setWhatsappAgent('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal disconnect WhatsApp');
    } finally {
      setDisconnectingWhatsapp(false);
    }
  };

  const copyWhatsappLink = () => {
    if (whatsappStatus?.whatsapp_link) {
      navigator.clipboard.writeText(whatsappStatus.whatsapp_link);
      setCopiedLink(true);
      toast.success('Link disalin!');
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  // Auto-save Embedded WhatsApp agent
  const handleAutoSaveEmbeddedAgent = async (newAgentId) => {
    setWaEmbeddedAgent(newAgentId);
    if (!newAgentId) return;
    setSavingEmbeddedAgent(true);
    try {
      await axios.post(`${API}/whatsapp-embedded/set-agent`, { user_id: user?.id, chatbot_id: newAgentId }, { headers });
      toast.success('AI Agent WhatsApp tersimpan');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan AI Agent');
    } finally {
      setSavingEmbeddedAgent(false);
    }
  };

  const handleDisconnectEmbedded = async () => {
    if (!confirm('Yakin ingin memutuskan WhatsApp Cloud API?')) return;
    setDisconnectingEmbedded(true);
    try {
      await axios.delete(`${API}/whatsapp-embedded/disconnect?user_id=${user?.id}`, { headers });
      toast.success('WhatsApp Cloud API disconnected');
      setWaEmbeddedStatus(null);
      setWaEmbeddedAgent('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal disconnect WhatsApp');
    } finally {
      setDisconnectingEmbedded(false);
    }
  };

  // Instagram handlers
  const handleConnectInstagram = () => {
    if (!user?.id) { toast.error('Silakan login terlebih dahulu'); return; }
    const instagramAppId = '1183763387300782';
    const redirectUri = 'https://nerava.id/api/instagram/oauth/callback';
    const scopes = ['instagram_business_basic', 'instagram_business_manage_messages'].join('%2C');
    const state = user.id;
    const instagramOAuthUrl = `https://www.instagram.com/oauth/authorize?force_reauth=true&client_id=${instagramAppId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${scopes}&state=${state}`;
    window.location.href = instagramOAuthUrl;
  };

  const [connectingInstagramDirect, setConnectingInstagramDirect] = useState(false);
  
  const handleConnectInstagramDirect = async () => {
    if (!user?.id) { toast.error('Silakan login terlebih dahulu'); return; }
    try {
      setConnectingInstagramDirect(true);
      const response = await axios.post(`${API}/instagram/connect-with-token`, {}, { headers });
      if (response.data?.success) {
        toast.success(`Instagram @${response.data.profile?.username || 'account'} connected!`);
        fetchAllData();
      } else {
        toast.error('Gagal menghubungkan Instagram');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghubungkan Instagram');
    } finally {
      setConnectingInstagramDirect(false);
    }
  };

  // Instagram Demo
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoSenderName, setDemoSenderName] = useState('Budi Santoso');
  const [demoMessage, setDemoMessage] = useState('Halo, saya tertarik dengan produk Anda. Ada apa saja?');
  const [sendingDemo, setSendingDemo] = useState(false);
  const [demoResult, setDemoResult] = useState(null);

  const handleSendDemo = async () => {
    if (!demoMessage.trim()) { toast.error('Pesan tidak boleh kosong'); return; }
    try {
      setSendingDemo(true);
      setDemoResult(null);
      const response = await axios.post(`${API}/instagram/simulate-dm`, { sender_name: demoSenderName || 'Demo Customer', message: demoMessage }, { headers });
      if (response.data?.success) {
        setDemoResult(response.data);
        toast.success('Demo berhasil! Cek Inbox untuk melihat conversation.');
      } else {
        toast.error('Gagal mengirim demo');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengirim demo. Pastikan AI Agent sudah dipilih.');
    } finally {
      setSendingDemo(false);
    }
  };

  const subscribeInstagramWebhook = async () => {
    try {
      setSubscribingWebhook(true);
      const response = await axios.post(`${API}/instagram/subscribe-webhook`, {}, { headers });
      if (response.data?.success) {
        toast.success('Webhook Instagram aktif! Anda bisa menerima DM.');
        fetchAllData();
      }
    } catch (error) {
      console.error('Webhook subscription error:', error);
    } finally {
      setSubscribingWebhook(false);
    }
  };

  // Auto-save Instagram agent
  const handleAutoSaveInstagramAgent = async (newAgentId) => {
    setInstagramAgent(newAgentId);
    if (!newAgentId) return;
    try {
      setSavingInstagram(true);
      await axios.put(`${API}/instagram/update-agent`, { chatbot_id: newAgentId }, { headers });
      toast.success('AI Agent Instagram tersimpan');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan AI Agent');
    } finally {
      setSavingInstagram(false);
    }
  };

  const handleDisconnectInstagram = async () => {
    if (!confirm('Yakin ingin memutuskan Instagram?')) return;
    try {
      setDisconnectingInstagram(true);
      await axios.delete(`${API}/instagram/disconnect`, { headers });
      toast.success('Instagram disconnected');
      setInstagramStatus({ connected: false });
      setInstagramAgent('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal disconnect Instagram');
    } finally {
      setDisconnectingInstagram(false);
    }
  };

  // Website handlers
  const handleShowEmbed = () => {
    if (!websiteAgent) { toast.error('Pilih AI Agent terlebih dahulu'); return; }
    const code = `<!-- Nerava Chat Widget -->\n<script>\n  window.NERAVA_CONFIG = {\n    chatbotId: "${websiteAgent}",\n    position: "bottom-right",\n    primaryColor: "#10b981"\n  };\n</script>\n<script src="${backendUrl}/widget.js" async></script>`;
    setEmbedCode(code);
    setShowEmbedModal(true);
  };

  const handleCopyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      toast.success('Kode embed disalin!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Gagal menyalin kode');
    }
  };

  // Count active channels
  const activeChannelCount = [
    messengerStatus?.connected,
    instagramStatus?.connected,
    whatsappStatus?.connected || whatsappQrStatus?.connected || waEmbeddedStatus?.connected,
    tiktokStatus?.connected
  ].filter(Boolean).length;

  const isAnyWhatsappConnected = whatsappStatus?.connected || whatsappQrStatus?.connected || waEmbeddedStatus?.connected;
  const isAnyChannelConnected = isAnyWhatsappConnected || messengerStatus?.connected || instagramStatus?.connected;

  const getWhatsappModeName = () => {
    if (waEmbeddedStatus?.connected) return 'Cloud API';
    if (whatsappQrStatus?.connected) return 'QR Code';
    if (whatsappStatus?.mode === 'shared') return 'Nerava';
    if (whatsappStatus?.connected) return 'Cloud API';
    return null;
  };

  // Saving indicator component
  const SavingBadge = ({ saving }) => {
    if (!saving) return null;
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-indigo-600 dark:text-indigo-400 font-medium">
        <Loader2 className="w-2.5 h-2.5 animate-spin" /> Menyimpan...
      </span>
    );
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'dark' : ''}`} data-testid="channels-page">
      <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A] transition-colors overflow-x-hidden">
        <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <div className="lg:ml-64">
          {/* Header */}
          <header className="sticky top-0 backdrop-blur-xl bg-white/85 dark:bg-[#0A0A0A]/85 border-b border-slate-200 dark:border-zinc-800/80 z-20">
            <div className="px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-slate-600 dark:text-zinc-300" data-testid="sidebar-toggle">
                    <Menu className="w-5 h-5" />
                  </button>
                  <div className="min-w-0">
                    <h1 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50">
                      Channel Management
                    </h1>
                    <p className="text-xs sm:text-sm text-slate-500 dark:text-zinc-400 mt-0.5 hidden sm:block">
                      Kelola semua channel komunikasi bisnis Anda
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </header>

          <main className="p-4 sm:p-6 lg:p-8">
            {loading ? (
              <div className="flex items-center justify-center py-16 sm:py-24">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600 dark:text-indigo-400" />
              </div>
            ) : (
            <div className="max-w-5xl mx-auto space-y-6">
              <WhatsAppEmbeddedSignup variant="banner" />
              
              {/* Stats Summary - Compact Row */}
              <div className="grid grid-cols-3 gap-3" data-testid="channel-stats">
                <div className="bg-white dark:bg-zinc-900 rounded-xl p-3 sm:p-4 border border-slate-100 dark:border-zinc-800/60">
                  <div className="flex items-center gap-2 mb-1">
                    <Radio className="w-3.5 h-3.5 text-emerald-500" />
                    <span className="text-[10px] sm:text-xs font-medium text-slate-500 dark:text-zinc-400 uppercase tracking-wide">Aktif</span>
                  </div>
                  <p className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50" data-testid="active-channel-count">
                    {activeChannelCount}<span className="text-xs font-normal text-slate-500 ml-0.5 dark:text-zinc-400">/4</span>
                  </p>
                </div>
                <div className="bg-white dark:bg-zinc-900 rounded-xl p-3 sm:p-4 border border-slate-100 dark:border-zinc-800/60">
                  <div className="flex items-center gap-2 mb-1">
                    <Bot className="w-3.5 h-3.5 text-blue-500" />
                    <span className="text-[10px] sm:text-xs font-medium text-slate-500 dark:text-zinc-400 uppercase tracking-wide">AI Agent</span>
                  </div>
                  <p className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50" data-testid="ai-agent-count">
                    {chatbots.length}
                  </p>
                </div>
                <div className="bg-white dark:bg-zinc-900 rounded-xl p-3 sm:p-4 border border-slate-100 dark:border-zinc-800/60">
                  <div className="flex items-center gap-2 mb-1">
                    <Zap className="w-3.5 h-3.5 text-amber-500" />
                    <span className="text-[10px] sm:text-xs font-medium text-slate-500 dark:text-zinc-400 uppercase tracking-wide">Status</span>
                  </div>
                  <p className={`text-sm sm:text-base font-bold ${isAnyChannelConnected ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500 dark:text-zinc-400'}`} data-testid="channel-status">
                    {isAnyChannelConnected ? 'Online' : 'Offline'}
                  </p>
                </div>
              </div>

              {/* Connected Channels */}
              {(messengerStatus?.connected || instagramStatus?.connected || isAnyWhatsappConnected) && (
                <section data-testid="connected-channels-section">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    <h2 className="text-sm font-semibold text-slate-900 dark:text-zinc-50 uppercase tracking-wide">Channel Terhubung</h2>
                  </div>
                  <div className="space-y-3">

                    {/* WhatsApp Cloud API (Embedded) */}
                    {waEmbeddedStatus?.connected && (
                      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-100 dark:border-zinc-800/60 overflow-hidden" data-testid="connected-whatsapp-embedded">
                        <div className="p-4">
                          {/* Header row */}
                          <div className="flex items-center justify-between gap-3 mb-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center shrink-0">
                                <FaWhatsapp className="w-5 h-5 text-white" />
                              </div>
                              <div className="min-w-0">
                                <h3 className="text-sm font-semibold text-slate-900 dark:text-zinc-50">WhatsApp Cloud API</h3>
                                <p className="text-xs text-slate-500 dark:text-zinc-400 truncate">
                                  {waEmbeddedStatus.phone_number || waEmbeddedStatus.display_phone_number || 'Nomor Anda'}
                                </p>
                              </div>
                            </div>
                            {waEmbeddedStatus.needs_reconnect ? (
                              <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-[10px] font-semibold border border-red-200 dark:border-red-800" data-testid="wa-embedded-badge-reconnect">
                                <XCircle className="w-2.5 h-2.5" /> Perlu dihubungkan ulang
                              </span>
                            ) : (
                              <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-[10px] font-semibold border border-emerald-200 dark:border-emerald-800">
                                <CheckCircle className="w-2.5 h-2.5" /> Connected
                              </span>
                            )}
                          </div>

                          {/* Info row */}
                          <div className="flex items-center gap-4 py-2 border-t border-slate-100 dark:border-zinc-800/60 text-xs">
                            <div className="flex-1 min-w-0">
                              <span className="text-slate-500 dark:text-zinc-500">Nomor</span>
                              <p className="font-medium text-slate-700 dark:text-zinc-200 truncate">
                                {waEmbeddedStatus.phone_number || waEmbeddedStatus.display_phone_number || '—'}
                              </p>
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="text-slate-500 dark:text-zinc-500">AI Agent</span>
                              <div className="flex items-center gap-1">
                                <p className="font-medium text-slate-700 dark:text-zinc-200 truncate">
                                  {chatbots.find(b => b.id === waEmbeddedAgent)?.name || '—'}
                                </p>
                                <SavingBadge saving={savingEmbeddedAgent} />
                              </div>
                            </div>
                          </div>

                          {/* Agent selector */}
                          <div className="pt-3 border-t border-slate-100 dark:border-zinc-800/60">
                            <label className="text-[10px] font-medium text-slate-500 dark:text-zinc-500 uppercase tracking-wide mb-1.5 block">Ganti AI Agent</label>
                            <div className="flex items-center gap-2">
                              <Select value={waEmbeddedAgent} onValueChange={handleAutoSaveEmbeddedAgent} data-testid="wa-embedded-agent-select">
                                <SelectTrigger className="flex-1 h-9 text-xs bg-slate-50 dark:bg-zinc-800/50 border-slate-200 dark:border-zinc-700 rounded-lg">
                                  <SelectValue placeholder="Pilih AI Agent" />
                                </SelectTrigger>
                                <SelectContent>
                                  {chatbots.map((bot) => (
                                    <SelectItem key={bot.id} value={bot.id}>{bot.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={handleDisconnectEmbedded}
                                disabled={disconnectingEmbedded}
                                className="h-9 w-9 p-0 text-red-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg shrink-0 dark:text-red-400 dark:bg-red-950/40"
                                data-testid="disconnect-wa-embedded-btn"
                              >
                                {disconnectingEmbedded ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* WhatsApp Shared/QR */}
                    {!waEmbeddedStatus?.connected && (whatsappStatus?.connected || whatsappQrStatus?.connected) && (
                      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-100 dark:border-zinc-800/60 overflow-hidden" data-testid="connected-whatsapp">
                        <div className="p-4">
                          {/* Header row */}
                          <div className="flex items-center justify-between gap-3 mb-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center shrink-0">
                                <FaWhatsapp className="w-5 h-5 text-white" />
                              </div>
                              <div className="min-w-0">
                                <h3 className="text-sm font-semibold text-slate-900 dark:text-zinc-50">WhatsApp Business</h3>
                                <p className="text-xs text-slate-500 dark:text-zinc-400 truncate">
                                  {whatsappQrStatus?.connected 
                                    ? (whatsappQrStatus.phone_name || whatsappQrStatus.phone_number || 'Nomor Anda')
                                    : `Toko: ${whatsappStatus?.store_name || '-'}`}
                                </p>
                              </div>
                            </div>
                            <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-[10px] font-semibold border border-emerald-200 dark:border-emerald-800">
                              <CheckCircle className="w-2.5 h-2.5" />
                              {whatsappQrStatus?.connected ? 'QR' : whatsappStatus?.mode === 'shared' ? 'Nerava' : 'API'}
                            </span>
                          </div>

                          {/* Info row */}
                          <div className="flex items-center gap-4 py-2 border-t border-slate-100 dark:border-zinc-800/60 text-xs">
                            <div className="flex-1 min-w-0">
                              <span className="text-slate-500 dark:text-zinc-500">Nomor Aktif</span>
                              <p className="font-medium text-slate-700 dark:text-zinc-200 truncate">
                                {whatsappQrStatus?.connected 
                                  ? (whatsappQrStatus.phone_number || 'Nomor Anda')
                                  : (whatsappStatus?.shared_phone || '+62xxx')}
                              </p>
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="text-slate-500 dark:text-zinc-500">AI Agent</span>
                              <div className="flex items-center gap-1">
                                <p className="font-medium text-slate-700 dark:text-zinc-200 truncate">
                                  {whatsappStatus?.chatbot_name || chatbots.find(b => b.id === whatsappAgent)?.name || '—'}
                                </p>
                                <SavingBadge saving={savingWhatsapp} />
                              </div>
                            </div>
                          </div>

                          {/* WhatsApp Link */}
                          {whatsappStatus?.connected && whatsappStatus.whatsapp_link && whatsappStatus.mode === 'shared' && (
                            <div className="flex items-center gap-2 py-2 border-t border-slate-100 dark:border-zinc-800/60">
                              <Link2 className="w-3.5 h-3.5 text-slate-500 shrink-0 dark:text-zinc-400" />
                              <code className="flex-1 text-[11px] text-slate-600 dark:text-zinc-300 truncate" data-testid="whatsapp-link">
                                {whatsappStatus.whatsapp_link}
                              </code>
                              <button
                                onClick={copyWhatsappLink}
                                className="shrink-0 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-800 transition-colors dark:bg-zinc-800"
                                data-testid="copy-whatsapp-link-btn"
                              >
                                {copiedLink 
                                  ? <Check className="w-3.5 h-3.5 text-emerald-500" /> 
                                  : <Copy className="w-3.5 h-3.5 text-slate-500 dark:text-zinc-400" />}
                              </button>
                            </div>
                          )}

                          {/* Agent selector */}
                          <div className="pt-3 border-t border-slate-100 dark:border-zinc-800/60">
                            <label className="text-[10px] font-medium text-slate-500 dark:text-zinc-500 uppercase tracking-wide mb-1.5 block">Ganti AI Agent</label>
                            <div className="flex items-center gap-2">
                              <Select value={whatsappAgent} onValueChange={handleAutoSaveWhatsappAgent} data-testid="wa-agent-select">
                                <SelectTrigger className="flex-1 h-9 text-xs bg-slate-50 dark:bg-zinc-800/50 border-slate-200 dark:border-zinc-700 rounded-lg">
                                  <SelectValue placeholder="Pilih AI Agent" />
                                </SelectTrigger>
                                <SelectContent>
                                  {chatbots.map((bot) => (
                                    <SelectItem key={bot.id} value={bot.id}>{bot.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={whatsappQrStatus?.connected ? handleDisconnectWhatsappQr : handleDisconnectWhatsapp}
                                disabled={disconnectingWhatsapp}
                                className="h-9 w-9 p-0 text-red-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg shrink-0 dark:text-red-400 dark:bg-red-950/40"
                                data-testid="disconnect-whatsapp-btn"
                              >
                                {disconnectingWhatsapp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Connected Messenger */}
                    {messengerStatus?.connected && (
                      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-100 dark:border-zinc-800/60 overflow-hidden" data-testid="connected-messenger">
                        <div className="p-4">
                          <div className="flex items-center justify-between gap-3 mb-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-10 h-10 bg-indigo-500 rounded-xl flex items-center justify-center shrink-0">
                                <Facebook className="w-5 h-5 text-white" />
                              </div>
                              <div className="min-w-0">
                                <h3 className="text-sm font-semibold text-slate-900 dark:text-zinc-50">Facebook Messenger</h3>
                                <p className="text-xs text-slate-500 dark:text-zinc-400 truncate">
                                  {messengerStatus.page_name}
                                </p>
                              </div>
                            </div>
                            <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-[10px] font-semibold border border-emerald-200 dark:border-emerald-800">
                              <CheckCircle className="w-2.5 h-2.5" /> Connected
                            </span>
                          </div>

                          <div className="flex items-center gap-4 py-2 border-t border-slate-100 dark:border-zinc-800/60 text-xs">
                            <div className="flex-1 min-w-0">
                              <span className="text-slate-500 dark:text-zinc-500">Page ID</span>
                              <p className="font-medium text-slate-700 dark:text-zinc-200 truncate">{messengerStatus.page_id || '—'}</p>
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="text-slate-500 dark:text-zinc-500">AI Agent</span>
                              <div className="flex items-center gap-1">
                                <p className="font-medium text-slate-700 dark:text-zinc-200 truncate">
                                  {chatbots.find(b => b.id === messengerAgent)?.name || '—'}
                                </p>
                                <SavingBadge saving={savingMessenger} />
                              </div>
                            </div>
                          </div>

                          {/* FB Page Link */}
                          {messengerStatus.page_id && (
                            <div className="flex items-center gap-2 py-2 border-t border-slate-100 dark:border-zinc-800/60">
                              <Link2 className="w-3.5 h-3.5 text-slate-500 shrink-0 dark:text-zinc-400" />
                              <code className="flex-1 text-[11px] text-slate-600 dark:text-zinc-300 truncate">
                                fb.com/{messengerStatus.page_id}
                              </code>
                              <button
                                onClick={() => window.open(`https://facebook.com/${messengerStatus.page_id}`, '_blank')}
                                className="shrink-0 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-800 transition-colors dark:bg-zinc-800"
                                data-testid="open-fb-page-btn"
                              >
                                <ExternalLink className="w-3.5 h-3.5 text-slate-500 dark:text-zinc-400" />
                              </button>
                            </div>
                          )}

                          <div className="pt-3 border-t border-slate-100 dark:border-zinc-800/60">
                            <label className="text-[10px] font-medium text-slate-500 dark:text-zinc-500 uppercase tracking-wide mb-1.5 block">Ganti AI Agent</label>
                            <div className="flex items-center gap-2">
                              <Select value={messengerAgent} onValueChange={handleAutoSaveMessengerAgent} data-testid="messenger-agent-select">
                                <SelectTrigger className="flex-1 h-9 text-xs bg-slate-50 dark:bg-zinc-800/50 border-slate-200 dark:border-zinc-700 rounded-lg">
                                  <SelectValue placeholder="Pilih AI Agent" />
                                </SelectTrigger>
                                <SelectContent>
                                  {chatbots.map((bot) => (
                                    <SelectItem key={bot.id} value={bot.id}>{bot.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={handleDisconnectMessenger}
                                disabled={disconnectingMessenger}
                                className="h-9 w-9 p-0 text-red-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg shrink-0 dark:text-red-400 dark:bg-red-950/40"
                                data-testid="disconnect-messenger-btn"
                              >
                                {disconnectingMessenger ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Connected Instagram */}
                    {instagramStatus?.connected && (
                      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-100 dark:border-zinc-800/60 overflow-hidden" data-testid="connected-instagram">
                        <div className="p-4">
                          <div className="flex items-center justify-between gap-3 mb-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shrink-0">
                                <Instagram className="w-5 h-5 text-white" />
                              </div>
                              <div className="min-w-0">
                                <h3 className="text-sm font-semibold text-slate-900 dark:text-zinc-50">Instagram DM</h3>
                                <p className="text-xs text-slate-500 dark:text-zinc-400 truncate">
                                  @{instagramStatus.platform_name || 'instagram'}
                                </p>
                              </div>
                            </div>
                            <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-[10px] font-semibold border border-emerald-200 dark:border-emerald-800">
                              <CheckCircle className="w-2.5 h-2.5" /> Connected
                            </span>
                          </div>

                          <div className="flex items-center gap-4 py-2 border-t border-slate-100 dark:border-zinc-800/60 text-xs">
                            <div className="flex-1 min-w-0">
                              <span className="text-slate-500 dark:text-zinc-500">Username</span>
                              <p className="font-medium text-slate-700 dark:text-zinc-200 truncate">@{instagramStatus.platform_name || '—'}</p>
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="text-slate-500 dark:text-zinc-500">AI Agent</span>
                              <div className="flex items-center gap-1">
                                <p className="font-medium text-slate-700 dark:text-zinc-200 truncate">
                                  {chatbots.find(b => b.id === instagramAgent)?.name || '—'}
                                </p>
                                <SavingBadge saving={savingInstagram} />
                              </div>
                            </div>
                          </div>

                          {/* IG Profile Link */}
                          {instagramStatus.platform_name && (
                            <div className="flex items-center gap-2 py-2 border-t border-slate-100 dark:border-zinc-800/60">
                              <Link2 className="w-3.5 h-3.5 text-slate-500 shrink-0 dark:text-zinc-400" />
                              <code className="flex-1 text-[11px] text-slate-600 dark:text-zinc-300 truncate">
                                instagram.com/{instagramStatus.platform_name}
                              </code>
                              <button
                                onClick={() => window.open(`https://instagram.com/${instagramStatus.platform_name}`, '_blank')}
                                className="shrink-0 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-zinc-800 transition-colors dark:bg-zinc-800"
                                data-testid="open-ig-profile-btn"
                              >
                                <ExternalLink className="w-3.5 h-3.5 text-slate-500 dark:text-zinc-400" />
                              </button>
                            </div>
                          )}

                          <div className="pt-3 border-t border-slate-100 dark:border-zinc-800/60">
                            <label className="text-[10px] font-medium text-slate-500 dark:text-zinc-500 uppercase tracking-wide mb-1.5 block">Ganti AI Agent</label>
                            <div className="flex items-center gap-2">
                              <Select value={instagramAgent} onValueChange={handleAutoSaveInstagramAgent} data-testid="ig-agent-select">
                                <SelectTrigger className="flex-1 h-9 text-xs bg-slate-50 dark:bg-zinc-800/50 border-slate-200 dark:border-zinc-700 rounded-lg">
                                  <SelectValue placeholder="Pilih AI Agent" />
                                </SelectTrigger>
                                <SelectContent>
                                  {chatbots.map((bot) => (
                                    <SelectItem key={bot.id} value={bot.id}>{bot.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={handleDisconnectInstagram}
                                disabled={disconnectingInstagram}
                                className="h-9 w-9 p-0 text-red-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg shrink-0 dark:text-red-400 dark:bg-red-950/40"
                                data-testid="disconnect-ig-btn"
                              >
                                {disconnectingInstagram ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
                              </Button>
                            </div>
                          </div>


                        </div>
                      </div>
                    )}

                  </div>
                </section>
              )}

              {/* Available Channels */}
              <section data-testid="available-channels-section">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-zinc-700" />
                  <h2 className="text-sm font-semibold text-slate-900 dark:text-zinc-50 uppercase tracking-wide">Channel Tersedia</h2>
                </div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  
                  {/* WhatsApp */}
                  {!whatsappStatus?.connected && !whatsappQrStatus?.connected && !waEmbeddedStatus?.connected && (
                    <AvailableChannelCard
                      icon={FaWhatsapp}
                      iconBgColor="bg-green-500"
                      name="WhatsApp Business"
                      description="Auto-reply pelanggan via WhatsApp dengan AI"
                      onConnect={() => setShowWaMethodModal(true)}
                      features={['Auto Reply 24/7', 'AI Customer Service', 'Broadcast']}
                    />
                  )}
                  
                  {/* Messenger */}
                  {!messengerStatus?.connected && (
                    <AvailableChannelCard
                      icon={Facebook}
                      iconBgColor="bg-indigo-600"
                      name="Facebook Messenger"
                      description="Auto-reply Facebook Page messages"
                      onConnect={handleConnectMessenger}
                      features={['Page Integration', 'Auto Response', 'Lead Capture']}
                    />
                  )}
                  
                  {/* Instagram */}
                  {!instagramStatus?.connected && (
                    <AvailableChannelCard
                      icon={Instagram}
                      iconBgColor="bg-gradient-to-br from-purple-500 to-pink-500"
                      name="Instagram DM"
                      description="Balas Direct Message Instagram otomatis"
                      onConnect={handleConnectInstagram}
                      features={['DM Auto Reply', 'Story Mentions', 'Quick Responses']}
                    />
                  )}

                  {/* Website Chat Widget */}
                  <AvailableChannelCard
                    icon={Globe}
                    iconBgColor="bg-slate-700"
                    name="Website Chat"
                    description="Embed chat widget di website Anda"
                    onConnect={() => setShowEmbedModal(true)}
                    connectLabel="Dapatkan Kode Embed"
                    features={['Chat Widget', 'Customizable', 'Lead Generation']}
                  />
                  
                </div>
              </section>
            </div>
            )}
          </main>
        </div>
      </div>

      {/* === MODALS === */}

      {/* Embed Code Modal */}
      <Dialog open={showEmbedModal} onOpenChange={setShowEmbedModal}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Code className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              Kode Embed Widget
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-2 space-y-3">
            <div>
              <label className="text-[10px] font-medium text-slate-500 dark:text-zinc-500 uppercase tracking-wide mb-1.5 block">Pilih AI Agent</label>
              <Select value={websiteAgent} onValueChange={setWebsiteAgent} data-testid="website-agent-select">
                <SelectTrigger className="w-full h-9 text-xs bg-slate-50 dark:bg-zinc-800/50 border-slate-200 dark:border-zinc-700 rounded-lg">
                  <SelectValue placeholder="Pilih AI Agent" />
                </SelectTrigger>
                <SelectContent>
                  {chatbots.map((bot) => (
                    <SelectItem key={bot.id} value={bot.id}>{bot.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {websiteAgent && (
              <>
                <div className="relative">
                  <pre className="bg-slate-900 dark:bg-slate-950 text-green-400 p-4 rounded-xl text-xs overflow-x-auto whitespace-pre-wrap break-all font-mono">
                    {`<!-- Nerava Chat Widget -->\n<script>\n  window.NERAVA_CONFIG = {\n    chatbotId: "${websiteAgent}",\n    position: "bottom-right",\n    primaryColor: "#10b981"\n  };\n</script>\n<script src="${backendUrl}/widget.js" async></script>`}
                  </pre>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      const code = `<!-- Nerava Chat Widget -->\n<script>\n  window.NERAVA_CONFIG = {\n    chatbotId: "${websiteAgent}",\n    position: "bottom-right",\n    primaryColor: "#10b981"\n  };\n</script>\n<script src="${backendUrl}/widget.js" async></script>`;
                      navigator.clipboard.writeText(code);
                      setCopied(true);
                      toast.success('Kode embed disalin!');
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className="absolute top-2 right-2 gap-1 h-7 text-xs"
                    data-testid="copy-embed-btn"
                  >
                    {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copied ? 'Disalin!' : 'Salin'}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 dark:text-zinc-400">
                  Tempel kode ini sebelum tag {'</body>'} di website Anda.
                </p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Instagram Demo Modal */}
      <Dialog open={showDemoModal} onOpenChange={(open) => { setShowDemoModal(open); if (!open) setDemoResult(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Instagram className="w-4 h-4 text-pink-600 dark:text-pink-400" />
              Test Instagram DM
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-medium mb-1.5 block">Nama Pengirim</Label>
              <Input value={demoSenderName} onChange={(e) => setDemoSenderName(e.target.value)} placeholder="Budi Santoso" className="h-9 text-sm bg-slate-50 dark:bg-zinc-800/50 rounded-lg" />
            </div>
            <div>
              <Label className="text-xs font-medium mb-1.5 block">Pesan</Label>
              <Textarea value={demoMessage} onChange={(e) => setDemoMessage(e.target.value)} placeholder="Halo, saya tertarik..." rows={3} className="text-sm bg-slate-50 dark:bg-zinc-800/50 rounded-lg resize-none" />
            </div>
            {demoResult && (
              <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl">
                <p className="text-xs font-semibold text-emerald-800 dark:text-emerald-300 mb-1">AI Response:</p>
                <p className="text-xs text-emerald-700 dark:text-emerald-400">{demoResult.ai_response || 'Cek Inbox'}</p>
              </div>
            )}
          </div>
          <DialogFooter className="border-t border-slate-100 dark:border-zinc-800/60 pt-3">
            <Button onClick={handleSendDemo} disabled={sendingDemo} className="gap-1.5 bg-pink-600 hover:bg-pink-700 text-white" data-testid="send-demo-btn">
              {sendingDemo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sendingDemo ? 'Mengirim...' : 'Kirim Demo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Connect Modal - Shared Number */}
      <Dialog open={showWhatsappConnectModal} onOpenChange={setShowWhatsappConnectModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <FaWhatsapp className="w-4 h-4 text-green-600 dark:text-green-400" />
              Aktifkan WhatsApp Business
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-3">
              <p className="text-xs text-green-800 dark:text-green-200">
                <strong>Instant Activation!</strong> Gunakan nomor Nerava ({whatsappStatus?.shared_phone || '+62 851-1144-4413'}) untuk mulai chat dengan pelanggan.
              </p>
            </div>
            <div className="bg-slate-50 dark:bg-zinc-800/50 border border-slate-200 dark:border-zinc-700 rounded-xl p-3">
              <p className="text-[10px] text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">Nama Toko</p>
              {userStoreName ? (
                <p className="text-sm font-medium text-slate-900 dark:text-zinc-50">{userStoreName}</p>
              ) : (
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    Belum diisi. <a href="/settings/profile" className="underline font-medium">Isi di Profil</a>
                  </p>
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs font-medium mb-1.5 block">AI Agent <span className="text-red-500">*</span></Label>
              <Select value={whatsappAgent} onValueChange={setWhatsappAgent}>
                <SelectTrigger className="w-full h-9 text-sm bg-slate-50 dark:bg-zinc-800/50 rounded-lg">
                  <SelectValue placeholder="Pilih AI Agent" />
                </SelectTrigger>
                <SelectContent>
                  {chatbots.map((bot) => (
                    <SelectItem key={bot.id} value={bot.id}>{bot.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 rounded-xl p-3">
              <p className="text-xs text-blue-800 dark:text-indigo-200">
                <strong>Cara kerja:</strong> Anda mendapat link WhatsApp unik. Customer yang chat via link tersebut otomatis terhubung ke toko Anda.
              </p>
            </div>
          </div>
          <DialogFooter className="border-t border-slate-100 dark:border-zinc-800/60 pt-3">
            <Button variant="outline" onClick={() => setShowWhatsappConnectModal(false)} className="rounded-xl">Batal</Button>
            <Button onClick={handleConfirmConnectShared} disabled={connectingWhatsapp || !whatsappAgent || !userStoreName} className="gap-1.5 bg-green-600 hover:bg-green-700 rounded-xl" data-testid="activate-wa-btn">
              {connectingWhatsapp ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              {connectingWhatsapp ? 'Mengaktifkan...' : 'Aktifkan Sekarang'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Own Number Registration Modal */}
      <Dialog open={showOwnNumberModal} onOpenChange={setShowOwnNumberModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <FaWhatsapp className="w-4 h-4 text-green-600 dark:text-green-400" />
              Daftarkan Nomor Sendiri
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5 dark:text-red-400" />
                <div>
                  <p className="text-xs font-semibold text-red-800 dark:text-red-200">Penting!</p>
                  <ul className="text-[11px] text-red-700 dark:text-red-300 mt-1 space-y-0.5 list-disc list-inside">
                    <li>Nomor akan <strong>logout dari HP</strong></li>
                    <li>Chat history di HP <strong>hilang</strong></li>
                    <li>Nomor hanya via Nerava</li>
                    <li>Verifikasi: <strong>1-3 hari kerja</strong></li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="bg-slate-50 dark:bg-zinc-800/50 border border-slate-200 dark:border-zinc-700 rounded-xl p-3">
              <p className="text-[10px] text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1">Nama Bisnis</p>
              {userStoreName ? (
                <p className="text-sm font-medium text-slate-900 dark:text-zinc-50">{userStoreName}</p>
              ) : (
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    Belum diisi. <a href="/settings/profile" className="underline font-medium">Isi di Profil</a>
                  </p>
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs font-medium mb-1.5 block">Nomor WhatsApp <span className="text-red-500">*</span></Label>
              <Input value={ownPhoneNumber} onChange={(e) => setOwnPhoneNumber(e.target.value)} placeholder="08123456789" className="h-9 text-sm bg-slate-50 dark:bg-zinc-800/50 rounded-lg" />
            </div>
            <div className="flex items-start gap-2">
              <input type="checkbox" id="confirm-own-number" checked={ownNumberConfirmed} onChange={(e) => setOwnNumberConfirmed(e.target.checked)} className="mt-1 rounded" />
              <label htmlFor="confirm-own-number" className="text-[11px] text-slate-600 dark:text-zinc-400 leading-tight">
                Saya paham nomor ini akan dimigrasi ke WhatsApp Cloud API dan tidak bisa digunakan di HP.
              </label>
            </div>
          </div>
          <DialogFooter className="border-t border-slate-100 dark:border-zinc-800/60 pt-3">
            <Button variant="outline" onClick={() => { setShowOwnNumberModal(false); setOwnNumberConfirmed(false); }} className="rounded-xl">Batal</Button>
            <Button onClick={handleRegisterOwnNumber} disabled={connectingWhatsapp || !ownPhoneNumber.trim() || !userStoreName || !ownNumberConfirmed} className="gap-1.5 bg-green-600 hover:bg-green-700 rounded-xl" data-testid="register-own-number-btn">
              {connectingWhatsapp ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              {connectingWhatsapp ? 'Mengirim...' : 'Daftarkan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Method Selection Modal */}
      <Dialog open={showWaMethodModal} onOpenChange={handleCloseWaMethodModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <FaWhatsapp className="w-4 h-4 text-green-600 dark:text-green-400" />
              Pilih Metode WhatsApp
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Option 1: Nerava */}
            <div
              className={`border rounded-xl p-3.5 cursor-pointer transition-all ${
                waMethod === 'nerava' ? 'border-green-500 bg-green-50/50 dark:bg-green-900/10' : 'border-slate-200 dark:border-zinc-800 hover:border-slate-300'
              }`}
              onClick={() => setWaMethod('nerava')}
              data-testid="wa-option-nerava"
            >
              <div className="flex items-start gap-3">
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center mt-0.5 shrink-0 ${
                  waMethod === 'nerava' ? 'border-green-500 bg-green-500' : 'border-slate-300 dark:border-zinc-700'
                }`}>
                  {waMethod === 'nerava' && <Check className="w-2.5 h-2.5 text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-900 dark:text-zinc-50">Nerava Number</p>
                    <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[10px] font-medium rounded-md">Rekomendasi</span>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-zinc-400 mt-0.5">+62 851-1144-4413</p>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5">
                    <span className="text-[10px] flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><Check className="w-2.5 h-2.5" /> Langsung aktif</span>
                    <span className="text-[10px] flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><Check className="w-2.5 h-2.5" /> Gratis</span>
                    <span className="text-[10px] flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><Check className="w-2.5 h-2.5" /> Broadcast</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Option 2: Cloud API */}
            <div
              className={`border rounded-xl p-3.5 cursor-pointer transition-all ${
                waMethod === 'cloud_api' ? 'border-green-500 bg-green-50/50 dark:bg-green-900/10' : 'border-slate-200 dark:border-zinc-800 hover:border-slate-300'
              }`}
              onClick={() => setWaMethod('cloud_api')}
              data-testid="wa-option-cloud-api"
            >
              <div className="flex items-start gap-3">
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center mt-0.5 shrink-0 ${
                  waMethod === 'cloud_api' ? 'border-green-500 bg-green-500' : 'border-slate-300 dark:border-zinc-700'
                }`}>
                  {waMethod === 'cloud_api' && <Check className="w-2.5 h-2.5 text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-900 dark:text-zinc-50">Nomor Sendiri (Official)</p>
                    <span className="px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-[10px] font-medium rounded-md">Meta Verified</span>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-zinc-400 mt-0.5">Connect via Meta Embedded Signup</p>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5">
                    <span className="text-[10px] flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><Check className="w-2.5 h-2.5" /> Fitur lengkap</span>
                    <span className="text-[10px] flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><Check className="w-2.5 h-2.5" /> Broadcast</span>
                    <span className="text-[10px] flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><Check className="w-2.5 h-2.5" /> Stabil</span>
                    <span className="text-[10px] flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><Check className="w-2.5 h-2.5" /> 3-5 menit</span>
                  </div>
                  {waMethod === 'cloud_api' && (
                    <div className="mt-3 space-y-2">
                      <div className="p-2.5 bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 rounded-lg">
                        <p className="text-[11px] text-blue-800 dark:text-indigo-200 font-medium mb-1">Yang Anda butuhkan:</p>
                        <ul className="text-[10px] text-indigo-700 dark:text-indigo-300 list-disc list-inside space-y-0.5">
                          <li>Akun Facebook</li>
                          <li>Nomor telepon</li>
                          <li>Akses SMS untuk verifikasi</li>
                        </ul>
                      </div>
                      <Button
                        onClick={() => { handleCloseWaMethodModal(); navigate('/whatsapp-onboarding'); }}
                        className="w-full h-9 bg-green-600 hover:bg-green-700 text-xs rounded-xl"
                        data-testid="start-cloud-api-setup-btn"
                      >
                        <FaFacebook className="w-3.5 h-3.5 mr-1.5" />
                        Mulai Setup via Facebook
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Comparison */}
          <div className="mt-3 p-3 bg-slate-50 dark:bg-zinc-900 rounded-xl">
            <p className="text-[10px] font-semibold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-2">Perbandingan</p>
            <div className="overflow-x-auto">
              <table className="w-full text-[10px] sm:text-xs">
                <thead>
                  <tr className="text-slate-500 dark:text-zinc-500">
                    <th className="text-left py-1 font-medium">Fitur</th>
                    <th className="text-center py-1 font-medium">Nerava</th>
                    <th className="text-center py-1 font-medium">Nomor Sendiri</th>
                  </tr>
                </thead>
                <tbody className="text-slate-600 dark:text-zinc-400">
                  <tr className="border-t border-slate-200/60 dark:border-zinc-800/40">
                    <td className="py-1.5">Setup</td>
                    <td className="text-center">Instant</td>
                    <td className="text-center">3-5 menit</td>
                  </tr>
                  <tr className="border-t border-slate-200/60 dark:border-zinc-800/40">
                    <td className="py-1.5">Broadcast</td>
                    <td className="text-center text-emerald-600 dark:text-emerald-400">Ya</td>
                    <td className="text-center text-emerald-600 dark:text-emerald-400">Ya</td>
                  </tr>
                  <tr className="border-t border-slate-200/60 dark:border-zinc-800/40">
                    <td className="py-1.5">Stabilitas</td>
                    <td className="text-center">Tinggi</td>
                    <td className="text-center">Tinggi</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <DialogFooter className="border-t border-slate-100 dark:border-zinc-800/60 pt-3 mt-2">
            <Button variant="outline" onClick={handleCloseWaMethodModal} className="flex-1 rounded-xl">Batal</Button>
            <Button
              onClick={handleSaveWaMethod}
              className="flex-1 bg-green-600 hover:bg-green-700 rounded-xl"
              disabled={waMethod === 'qr_code' && qrStatus !== 'connected' && qrCode}
              data-testid="save-wa-method-btn"
            >
              {waMethod === 'qr_code' && qrStatus !== 'connected' && qrCode ? 'Scan QR dulu...' : 'Lanjutkan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChannelsPage;
